import { fetchClinicEvents } from '@/entities/clinic';
import { EMITTER_CONSTANTS } from '@/shared/config';
import { emitter, showMessageError, adjustStudyTime, appearDataTable } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import type { EventsTabProps } from './eventsTab.types';

/**
 * Drives the event list, filters and paging of a study's events tab.
 * Fetches the study's clinic events on mount and when the study changes, applies UPDATE_EVENT_ECG
 * emitter updates to loaded rows, and exposes sort, dropdown, event id, date range, reset and
 * scroll-to-load-more handlers.
 *
 * @param props - Tab props carrying the study data and toggleECGViewer.
 * @returns The tab state, the range picker ref and the event list handlers.
 */
export const useEventsTab = ({
  data,
  toggleECGViewer,
}: Pick<EventsTabProps, 'data' | 'toggleECGViewer'>) => {
  /**
   * Produces the cleared event-list filter for a study's events tab. Keeps the study friendly id and ascending sort order while leaving cursor, event type, event id, search dates and the report-inclusion and full-data flags undefined.
   *
   * @returns Filter object with only the study id and sort order set.
   */
  const createInitialFilterObject = (): any => ({
    cursor: undefined,
    studyFid: data?.friendlyId,
    sortOrder: 'asc',
    isIncludedToReport: undefined,
    eventType: undefined,
    eventFid: undefined,
    startSearchDate: undefined,
    stopSearchDate: undefined,
    hasFullData: undefined,
  });

  const [state, setState] = useMergeState<any>({
    skeletonEventData: _.range(10).map(() => ({})),
    eventData: [],
    dataFilter: createInitialFilterObject(),
    isDisabledSearchInput: false,
    isLoadingMore: false,
  });

  const rangePickerRef = useRef<any>(null);
  const timeOutSearch = useRef<any>(null);
  const isLoading = useRef(false);

  /**
   * Applies a socket event update onto the already-loaded event list. Finds the row by id and, when present, overwrites type, process-later flag, process status, strip images, validity, full-data availability, report inclusion and comments with the values the message carries before writing the list back to state.
   *
   * @param msg - Socket message carrying the event id and its changed fields.
   * @returns Nothing.
   */
  const handleUpdateEventECG = (msg: any) => {
    const { eventData } = state;
    /**
     * The loaded event whose id matches the incoming update message. Resolves to undefined when the event list is absent or holds no such event.
     *
     * @returns The matching event row, or undefined when none matches.
     */
    const ele = eventData?.find((x: any) => x.id === msg.id);
    if (ele) {
      ele.type = msg.type || ele.type;
      ele.isProcessLater =
        msg.isProcessLater !== undefined ? msg.isProcessLater : ele.isProcessLater;
      ele.processStatus = msg.processStatus || ele.processStatus;
      ele.stripImgs = msg.stripImgs || ele.stripImgs;
      ele.isValid = msg.isValid !== undefined ? msg.isValid : ele.isValid;
      ele.hasFullData = msg.hasFullData !== undefined ? msg.hasFullData : ele.hasFullData;
      ele.isIncludedToReport =
        msg.isIncludedToReport !== undefined ? msg.isIncludedToReport : ele.isIncludedToReport;
      ele.comments = msg.comments !== undefined ? msg.comments : ele.comments;
      setState({ eventData });
    }
  };

  /**
   * Loads a study's clinic events for the events tab, either fresh or as the next page. Returns early without a study id or while a request is in flight, resets the cursor for a fresh load, converts the search date range back to server time using the study time offset, parses the event id filter into a number, and appends or replaces the rows, reporting failures as an error message.
   *
   * @param isLoadingMore - True to append the next page instead of replacing the list.
   * @returns A promise resolving once the event rows and the loading flags have been stored in state.
   */
  const fetchData = async (isLoadingMore?: boolean) => {
    const { dataFilter } = state;
    if (!dataFilter?.studyFid || isLoading.current) {
      return;
    }

    if (isLoadingMore) {
      setState({ isLoadingMore: true });
    } else {
      setState({ isLoadedEventData: false });
    }

    isLoading.current = true;
    _.assign(dataFilter, isLoadingMore ? {} : { cursor: undefined });

    const cloneDataFilter = _.cloneDeep(dataFilter);
    if (cloneDataFilter?.startSearchDate && cloneDataFilter?.stopSearchDate) {
      _.assign(cloneDataFilter, {
        startSearchDate: adjustStudyTime({
          dateMoment: cloneDataFilter.startSearchDate.startOf('day'),
          studyTimeOffset: data.studyTimeOffset,
          shouldRevertToServerTime: true,
        }).toISOString(),
        stopSearchDate: adjustStudyTime({
          dateMoment: cloneDataFilter.stopSearchDate.endOf('day'),
          studyTimeOffset: data.studyTimeOffset,
          shouldRevertToServerTime: true,
        }).toISOString(),
      });
    }

    try {
      const { eventData } = state;
      const queryFilter = _.assign({}, cloneDataFilter);
      queryFilter.eventFid = !queryFilter.eventFid ? undefined : parseInt(queryFilter.eventFid, 10);
      const newEvents = await fetchClinicEvents(queryFilter);
      setState({
        eventData: isLoadingMore ? appearDataTable(eventData, newEvents) : newEvents,
        isLoadedEventData: true,
        isLoadingMore: false,
      });
    } catch (error) {
      console.error('Failed to fetch events: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({ isLoadedEventData: true, isLoadingMore: false });
    }
    isLoading.current = false;
  };

  /**
   * Opens the ECG viewer for the clicked event row. Delegates to the viewer toggle with the event id.
   *
   * @param eventId - Event whose ECG should be displayed.
   * @returns Nothing.
   */
  const handleClickViewECG = (eventId: any) => {
    toggleECGViewer(eventId);
  };

  /**
   * Loads the next page of events once the list is scrolled to its bottom. Takes the friendly id of the last loaded event as the cursor and refetches in append mode, doing nothing while the list is empty.
   *
   * @returns Nothing.
   */
  const useBottomScrollListener = () => {
    const { eventData, dataFilter } = state;
    if (eventData?.length > 0) {
      const cursor = eventData?.[eventData.length - 1].friendlyId;
      _.assign(dataFilter, { cursor });
      fetchData(true);
    }
  };

  /**
   * Flips the event list between ascending and descending order. Ignores clicks while a fetch is running, toggles the filter's sortOrder, disables the search input and refetches the events.
   *
   * @returns Nothing.
   */
  const onClickSortOrder = () => {
    if (isLoading.current) {
      return;
    }

    const { dataFilter } = state;
    _.assign(dataFilter, {
      sortOrder: dataFilter.sortOrder === 'asc' ? 'desc' : 'asc',
    });
    setState({ isDisabledSearchInput: true });
    fetchData();
  };

  /**
   * Applies an event-list dropdown filter such as event type or report inclusion. Ignores changes while a fetch is running, stores the value under its filter key or undefined when 'All' is selected, disables the search input and refetches the events.
   *
   * @param name - Filter key being changed.
   * @param value - Selected values; 'All' clears the filter.
   * @returns Nothing.
   */
  const onChangeDropdown = (name: any, value: any) => {
    if (isLoading.current) {
      return;
    }

    const { dataFilter } = state;
    dataFilter[name] = value.includes('All') ? undefined : value;
    setState({ isDisabledSearchInput: true });
    fetchData();
  };

  /**
   * Searches the event list by event id. Resets the filter to its initial object keeping only the typed id, enables the search input, and debounces the refetch by 500ms after clearing any pending timer.
   *
   * @param name - Name of the search input; unused.
   * @param value - Typed event friendly id.
   * @returns Nothing.
   */
  const onSearchEventId = (name: any, value: any) => {
    const { dataFilter } = state;
    dataFilter.eventFid = value;
    _.assign(dataFilter, {
      ...createInitialFilterObject(),
      eventFid: value,
    });
    setState({ isDisabledSearchInput: false });

    if (timeOutSearch.current) {
      clearTimeout(timeOutSearch.current);
    }
    timeOutSearch.current = setTimeout(() => {
      fetchData();
      timeOutSearch.current = undefined;
    }, 500);
  };

  /**
   * Applies the picked date range to the event list filter. Acts only on a two-element range, writes its start and stop into the filter, disables the search input, refetches the events and blurs the range picker.
   *
   * @param dates - Selected start and stop range.
   * @returns Nothing.
   */
  const handleDatesChange = (dates: any) => {
    if (dates?.length === 2) {
      const startSearchDate = dates[0];
      const stopSearchDate = dates[1];
      const { dataFilter } = state;
      _.assign(dataFilter, {
        startSearchDate,
        stopSearchDate,
      });
      setState({ isDisabledSearchInput: true });
      fetchData();
      rangePickerRef.current.blur();
    }
  };

  /**
   * Reloads the event list after its filters have been reset. Triggers a fresh fetch with the current filter object.
   *
   * @returns Nothing.
   */
  const onClickReset = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    /**
     * The subscription registered on the shared emitter for UPDATE_EVENT_ECG notifications, routing them to the event-row update handler. Retained so the effect's cleanup can remove the listener when the events tab unmounts.
     *
     * @returns The emitter listener handle exposing remove().
     */
    const updateEventListener = emitter.addListener(
      EMITTER_CONSTANTS.UPDATE_EVENT_ECG,
      handleUpdateEventECG,
    );
    return () => {
      updateEventListener.remove();
    };
  }, []);

  useUpdateEffect(() => {
    if (data?.friendlyId !== state.dataFilter.studyFid) {
      _.assign(state.dataFilter, { studyFid: data?.friendlyId });
      fetchData();
    }
  }, [data, state.dataFilter]);

  return {
    state,
    rangePickerRef,
    handleClickViewECG,
    useBottomScrollListener,
    onClickSortOrder,
    onChangeDropdown,
    onSearchEventId,
    handleDatesChange,
    onClickReset,
  };
};
